import { Component, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ShamRatingStarIcon,
  ShamCalendarIcon,
  ShamPackageIcon,
} from 'sham-components/icons';
import { ProductCard } from '../../elements/product-card/product-card';
import { Product } from 'sham-components/shared/models';
import { LanguageService } from 'sham-components/services';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SellerProfileApi } from '@/app/services/seller-profile-api';
import { mediaUrl } from '@/app/utils/media-url';

@Component({
  selector: 'app-seller-profile',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ProductCard,
    ShamRatingStarIcon,
    ShamCalendarIcon,
    ShamPackageIcon,
    RouterLink,
  ],
  templateUrl: './seller-profile.html',
  styleUrl: './seller-profile.css',
})
export class SellerProfilePage {
  private langService = inject(LanguageService);
  private route = inject(ActivatedRoute);
  private sellerApi = inject(SellerProfileApi);
  private translate = inject(TranslateService);

  page = signal(1);
  pageSize = signal(20);
  totalCount = signal(0);
  isLoading = signal(false);

  currentLang = this.langService.currentLang;

  storeInfo = signal({
    name: '',
    logo: '',
    rating: 4.9,
    productsCount: 0,
    joinDate: '',
  });

  products = signal<Product[]>([]);

  sortOptions = signal([
    { label: 'Latest', value: 'latest' },
    { label: 'Cheapest', value: 'price-asc' },
    { label: 'Most Expensive', value: 'price-desc' },
  ]);

  ngOnInit() {
    const sellerId = this.route.snapshot.paramMap.get('sellerId');

    if (sellerId) {
      this.loadStoreInfo(sellerId);
      this.loadStoreProducts(sellerId, 1, true);
    }
  }

  private loadStoreInfo(sellerId: string) {
    this.sellerApi.getStoreInfo(sellerId).subscribe({
      next: (res) => {
        this.storeInfo.set({
          name: res.storeName,
          logo:
            mediaUrl(res.storeLogoUrl) ||
            'https://placehold.co/100x100/080d1a/white?text=Shop',
          rating: 4.9,
          productsCount: res.totalProductsCount,
          joinDate: this.formatYear(res.joinedDate),
        });
      },
      error: (err) => console.error('Failed to load store info:', err),
    });
  }

  loadMore() {
    if (this.isLoading()) return;
    if (this.products().length >= this.totalCount()) return;

    const sellerId = this.route.snapshot.paramMap.get('sellerId');
    if (!sellerId) return;

    const nextPage = this.page() + 1;

    this.loadStoreProducts(sellerId, nextPage, false);
  }

  @HostListener('window:scroll', [])
  onScroll() {
    const threshold = 150;
    const position = window.scrollY + window.innerHeight;
    const height = document.documentElement.scrollHeight;

    if (position >= height - threshold) {
      this.loadMore();
    }
  }

  private formatYear(dateString: string): string {
    if (!dateString) return '';
    const joinDate = new Date(dateString);
    const currentDate = new Date();
    const diffYears = currentDate.getFullYear() - joinDate.getFullYear();

    if (diffYears === 0) return this.translate.instant('This Year');
    if (diffYears === 1) return this.translate.instant('1 Year Ago');
    return this.translate.instant('Years Ago', { years: diffYears });
  }

  currentSort = signal<string>('newest');
  private loadStoreProducts(sellerId: string, page = 1, reset = true) {
    this.isLoading.set(true);

    this.sellerApi
      .getStoreProducts(sellerId, page, this.pageSize(), this.currentSort())
      .subscribe({
        next: (res) => {
          const products = res.items.map((item) => ({
            slug: item.slug,
            featuredImageUrl: item.thumbnailUrl || '',
            title: item.name,
            priceAmount: item.displayPrice,
            priceCurrency: item.currency,
            salesInfo:
              item.onSale && item.salePrice && item.regularPrice > item.salePrice
                ? {
                    amount: item.regularPrice - item.salePrice,
                    percentage: Math.round(
                      ((item.regularPrice - item.salePrice) / item.regularPrice) *
                        100,
                    ),
                  }
                : undefined,
            tag: item.onSale ? ('SALE' as const) : undefined,
          }));

          if (reset) {
            this.products.set(products);
          } else {
            this.products.update((prev) => [...prev, ...products]);
          }

          this.page.set(res.page);
          this.totalCount.set(res.totalCount);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error(err);
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newSort = selectElement.value;

    this.currentSort.set(newSort);

    const sellerId = this.route.snapshot.paramMap.get('sellerId');
    if (sellerId) {
      this.loadStoreProducts(sellerId, 1, true);
    }
  }
}
